/**
 * Diagnostic Search Utility
 * 
 * Use this to debug what text is actually in the PDFs
 * and why searches aren't finding expected content.
 */

/**
 * Find all occurrences of a search term in a document with surrounding context
 * @param {Object} doc - Document with textContent
 * @param {string} searchTerm - Term to search for
 * @param {number} contextChars - Characters of context to show around match
 * @returns {Array} Array of matches with context
 */
export const diagnosticSearch = (doc, searchTerm, contextChars = 200) => {
  if (!doc.textContent) return [];
  
  const text = doc.textContent;
  const lowerText = text.toLowerCase();
  const lowerTerm = searchTerm.toLowerCase();
  
  const results = [];
  let index = 0;
  
  while ((index = lowerText.indexOf(lowerTerm, index)) !== -1) {
    const start = Math.max(0, index - contextChars);
    const end = Math.min(text.length, index + lowerTerm.length + contextChars);
    
    results.push({
      position: index,
      before: text.substring(start, index),
      match: text.substring(index, index + lowerTerm.length),
      after: text.substring(index + lowerTerm.length, end)
    });
    
    index += lowerTerm.length;
  }
  
  return results;
};

/**
 * Search for multiple variations of a term
 * @param {Object} doc - Document with textContent
 * @param {Array} variations - Array of search term variations
 * @returns {Object} Results for each variation
 */
export const multiVariantSearch = (doc, variations) => {
  const results = {};
  
  variations.forEach(variant => {
    results[variant] = diagnosticSearch(doc, variant, 100);
  });
  
  return results;
};

/**
 * Find all instances of a word and show what comes before/after
 * @param {Object} doc - Document with textContent
 * @param {string} word - Single word to search for
 * @param {number} wordsBefore - Number of words to show before
 * @param {number} wordsAfter - Number of words to show after
 * @returns {Array} Array of word contexts
 */
export const wordContext = (doc, word, wordsBefore = 5, wordsAfter = 5) => {
  if (!doc.textContent) return [];
  
  const words = doc.textContent.split(/\s+/);
  const results = [];
  
  for (let i = 0; i < words.length; i++) {
    if (words[i].toLowerCase().includes(word.toLowerCase())) {
      const start = Math.max(0, i - wordsBefore);
      const end = Math.min(words.length, i + wordsAfter + 1);
      
      results.push({
        position: i,
        context: words.slice(start, end).join(' '),
        matchedWord: words[i]
      });
    }
  }
  
  return results;
};

/**
 * Check if a document contains table-like structures
 * @param {Object} doc - Document with textContent
 * @returns {Object} Table detection results
 */
export const detectTables = (doc) => {
  if (!doc.textContent) return { hasTables: false };
  
  const text = doc.textContent;
  
  // Look for common table indicators
  const tableIndicators = [
    /\btable\s+\d+/gi,
    /\|\s+\w+\s+\|/g,  // Pipe-separated
    /\t\w+\t/g,  // Tab-separated
    /\s{4,}\w+\s{4,}/g,  // Space-separated columns
  ];
  
  const matches = {};
  tableIndicators.forEach((pattern, idx) => {
    const found = text.match(pattern);
    matches[`pattern_${idx}`] = found ? found.length : 0;
  });
  
  // Look for "mm" and "m" (measurements common in tables)
  const measurements = text.match(/\d+mm/gi) || [];
  const distances = text.match(/\d+\.?\d*\s*m(?:etres?)?/gi) || [];
  
  return {
    hasTables: Object.values(matches).some(count => count > 0),
    tableIndicators: matches,
    measurements: measurements.length,
    distances: distances.length,
    sampleMeasurements: measurements.slice(0, 10),
    sampleDistances: distances.slice(0, 10)
  };
};

/**
 * Export for use in console/debugging
 */
export const runDiagnostics = (knowledgeBase, documentTitle, searchTerms) => {
  const doc = knowledgeBase.find(d => d.title.includes(documentTitle));
  
  if (!doc) {
    console.error(`Document not found: ${documentTitle}`);
    return null;
  }
  
  console.log(`\n🔬 DIAGNOSTIC RESULTS FOR: ${doc.title}`);
  console.log(`📄 Total characters: ${doc.textContent.length.toLocaleString()}`);
  console.log(`📄 Total words: ${doc.wordCount.toLocaleString()}`);
  console.log('\n');
  
  // Check for exact search terms
  searchTerms.forEach(term => {
    const results = diagnosticSearch(doc, term, 150);
    console.log(`🔍 Searching for: "${term}"`);
    console.log(`   Found: ${results.length} occurrences`);
    
    if (results.length > 0) {
      console.log(`   First occurrence at position: ${results[0].position}`);
      console.log(`   Context: ...${results[0].before}[${results[0].match}]${results[0].after}...`);
    } else {
      console.log(`   ❌ NOT FOUND!`);
      
      // Try variations
      const variations = [
        term,
        term.replace(/\s+/g, ''),  // No spaces
        term.replace(/\s+/g, '-'),  // Hyphenated
        term.replace(/\s+/g, '_'),  // Underscored
        term.toUpperCase(),
        term.charAt(0).toUpperCase() + term.slice(1)  // Title case
      ];
      
      console.log(`   Trying variations...`);
      variations.forEach(variant => {
        const varResults = diagnosticSearch(doc, variant, 50);
        if (varResults.length > 0) {
          console.log(`   ✅ Found "${variant}": ${varResults.length} times`);
          console.log(`      Sample: ...${varResults[0].before}[${varResults[0].match}]${varResults[0].after}...`);
        }
      });
    }
    console.log('\n');
  });
  
  // Check for individual words
  const singleWords = searchTerms.flatMap(term => term.split(/\s+/));
  singleWords.forEach(word => {
    const contexts = wordContext(doc, word, 3, 3);
    console.log(`📝 Word: "${word}" appears ${contexts.length} times`);
    if (contexts.length > 0 && contexts.length <= 20) {
      contexts.slice(0, 5).forEach(ctx => {
        console.log(`   ...${ctx.context}...`);
      });
    }
  });
  console.log('\n');
  
  // Table detection
  const tableInfo = detectTables(doc);
  console.log(`📊 TABLE DETECTION:`);
  console.log(`   Has tables: ${tableInfo.hasTables}`);
  console.log(`   Measurements: ${tableInfo.measurements}`);
  console.log(`   Distances: ${tableInfo.distances}`);
  if (tableInfo.sampleMeasurements.length > 0) {
    console.log(`   Sample measurements: ${tableInfo.sampleMeasurements.join(', ')}`);
  }
  if (tableInfo.sampleDistances.length > 0) {
    console.log(`   Sample distances: ${tableInfo.sampleDistances.join(', ')}`);
  }
  
  return {
    doc,
    searchResults: searchTerms.map(term => ({
      term,
      results: diagnosticSearch(doc, term)
    })),
    tableInfo
  };
};

